import React, { useEffect, useState } from "react";
import { FaRegClock } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { Link } from "react-router-dom";
import ModalComponent from "../../Assignments/Components/IconInfoRow.jsx";

const LEVEL_THEME = {
  Beginner: {
    chip: "bg-gray-100 text-gray-700 ring-1 ring-gray-200",
    dot: "bg-gray-500",
  },
  Intermediate: {
    chip: "bg-blue-50 text-blue-700 ring-1 ring-blue-100",
    dot: "bg-blue-500",
  },
  Advanced: {
    chip: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
    dot: "bg-rose-500",
  },
};

const METRICS_REVEAL_DELAY_MS = 240;
const METRICS_COUNT_DELAY_MS = 360;
const METRICS_COUNT_MAX_MS = 3600;

const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

/** Duration scales with target so large counts feel smooth but not slow. */
function metricsCountDurationMs(endValue) {
  return Math.min(METRICS_COUNT_MAX_MS, 900 + Math.cbrt(Math.max(endValue, 1)) * 700);
}

function useCountUp(endValue, delayMs = 0) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (endValue <= 0) {
      setDisplay(0);
      return undefined;
    }

    let cancelled = false;
    let raf = 0;
    let timeoutId;

    const startCount = () => {
      if (cancelled) return;
      const durationMs = metricsCountDurationMs(endValue);
      const start = performance.now();

      const tick = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - start) / durationMs);
        const eased = easeInOutCubic(t);
        const next = t >= 1 ? endValue : Math.round(eased * endValue);
        setDisplay(next);
        if (t < 1) raf = requestAnimationFrame(tick);
      };

      raf = requestAnimationFrame(tick);
    };

    setDisplay(0);
    if (delayMs > 0) {
      timeoutId = setTimeout(startCount, delayMs);
    } else {
      startCount();
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [endValue, delayMs]);

  return endValue <= 0 ? 0 : display;
}

const LANG_LABEL = {
  CPP: "C++",
  PYTHON: "Python",
  JAVA: "Java",
  C: "C",
  JS: "JavaScript",
  TS: "TypeScript",
  GO: "Go",
};

const CourseCard = ({
  name,
  short_description,
  time_period,
  course_id,
  onRemove,
  isSuperAdmin,
  level,
  languages = [],
  categories = [],
  benefits = [],
  numQuestions = 0,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [showCategoryPanel, setShowCategoryPanel] = useState(false);
  const [metricsReady, setMetricsReady] = useState(false);

  const handleDeleteClick = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setCourseToDelete(id);
    setOpenModal(true);
  };

  const handleToggleCategoryPanel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCategoryPanel((v) => !v);
  };

  const handleConfirmDelete = () => {
    if (onRemove && courseToDelete) {
      onRemove(courseToDelete);
    }
    setOpenModal(false);
  };

  const theme = LEVEL_THEME[level] || LEVEL_THEME.Beginner;
  const languageLabels = (languages || [])
    .map((l) => LANG_LABEL[l] || l)
    .filter(Boolean);
  const topicCount = categories?.length || 0;
  const categoryCountDisplay = useCountUp(topicCount, METRICS_COUNT_DELAY_MS);
  const questionCountDisplay = useCountUp(numQuestions, METRICS_COUNT_DELAY_MS);
  const categoryNames = (categories || [])
    .map((cat) => cat?.name || cat)
    .filter(Boolean);
  const hasBenefits = Array.isArray(benefits) && benefits.length > 0;
  const benefitRows = benefits || [];

  useEffect(() => {
    let timer;
    const raf = requestAnimationFrame(() => {
      timer = setTimeout(() => setMetricsReady(true), METRICS_REVEAL_DELAY_MS);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const metricTextMotion = metricsReady
    ? "opacity-100 translate-y-0"
    : "opacity-0 translate-y-1";

  /** Fixed heights so every card in a row lines up (title → desc → panel → footer). */
  const TITLE_BLOCK_MIN = "min-h-[46px]"; /* 2 lines @ ~18px */
  const DESC_BLOCK_MIN = "min-h-[92px]"; /* ~3.5 lines @ 14px × ~1.58 */
  const METRICS_BAR_MIN = "min-h-[44px]";
  const LANG_BLOCK_MIN = "min-h-[2.125rem]"; /* 2 lines of languages */

  return (
    <div
      className="group relative isolate mx-auto flex h-full min-h-0 w-full min-w-0 max-w-[min(100%,26rem)] flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {isSuperAdmin && onRemove && (
        <button
          type="button"
          onClick={(e) => handleDeleteClick(e, course_id)}
          aria-label="Delete course"
          className="pointer-events-auto absolute top-3 right-3 z-30 flex h-8 w-8 items-center justify-center rounded-lg bg-white text-gray-400 opacity-0 shadow-sm ring-1 ring-gray-200 transition-all group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 hover:ring-red-200"
        >
          <MdDelete className="h-4 w-4" />
        </button>
      )}

      <Link
        to={`/admin/courses/${course_id}`}
        className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white shadow-[0_2px_6px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:border-gray-300 hover:shadow-[0_12px_28px_rgba(15,23,42,0.10),0_4px_10px_rgba(15,23,42,0.06)]"
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4 pb-6 sm:p-[1.125rem] sm:pb-7">
          {/* Title — fixed slot (2 lines) */}
          <h3
            className={`mb-1 text-[18px] font-bold text-gray-900 leading-[1.28] line-clamp-2 group-hover:text-blue-700 transition-colors pr-8 ${TITLE_BLOCK_MIN}`}
          >
            {name}
          </h3>

          {/* Description — fixed slot (match tallest: 4 lines) */}
          <p
            className={`mt-0 text-[14px] font-medium leading-[1.58] text-gray-800 line-clamp-4 ${DESC_BLOCK_MIN}`}
          >
            {short_description?.trim()
              ? short_description
              : "\u00A0"}
          </p>

          {/* Categories & questions — pills on one row; expanded list on next row (full width) */}
          <div className="relative mt-2 flex w-full min-w-0 flex-col gap-1">
            <div
              className={`flex flex-wrap items-center gap-4 sm:gap-5 ${METRICS_BAR_MIN}`}
            >
              {topicCount > 0 ? (
                <button
                  type="button"
                  onClick={handleToggleCategoryPanel}
                  className="group inline-flex items-center gap-1.5 text-xs font-bold text-gray-900 transition-colors hover:text-gray-800"
                  aria-expanded={showCategoryPanel}
                  aria-label="Show categories"
                >
                  <span className="uppercase tracking-wide">Categories</span>
                  <span
                    className={`tabular-nums transition-all duration-800 ease-out ${metricTextMotion}`}
                  >
                    {categoryCountDisplay}
                  </span>
                  <svg
                    className="h-3 w-3 shrink-0 transition-transform"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      transform: showCategoryPanel
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              ) : (
                <span className="text-xs font-bold text-slate-400">
                  <span className="uppercase tracking-wide">Categories</span> —
                </span>
              )}

              {numQuestions > 0 ? (
                <span
                  className={`text-xs font-bold text-gray-900 tabular-nums ${metricTextMotion}`}
                >
                  <span className="uppercase tracking-wide">Questions</span>{" "}
                  {questionCountDisplay}
                </span>
              ) : (
                <span className="text-xs font-bold text-slate-400">
                  <span className="uppercase tracking-wide">Questions</span> —
                </span>
              )}
            </div>

            {topicCount > 0 && showCategoryPanel ? (
              <div
                className="w-full p-2 pl-0 pt-1"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <p className="mb-1.5 text-[11px] font-medium text-slate-500">
                  Category names
                </p>
                <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                  {categoryNames.map((cat, idx) => (
                    <li
                      key={`${cat}-${idx}`}
                      className="flex items-center gap-2 rounded-sm py-0.5 pr-2 text-[12.5px] text-slate-700 hover:bg-slate-50/80"
                    >
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-[10px] font-semibold tabular-nums text-slate-400">
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{cat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {/* Benefits — left-aligned header title */}
          <div
            className="relative mt-3 mb-2 flex min-h-0 flex-shrink-0 flex-col rounded-lg bg-white shadow-sm ring-1 ring-slate-200/90"
            aria-label="Benefits"
          >
            <div className="flex items-center border-b border-slate-100 bg-gradient-to-r from-sky-50/80 via-slate-50/70 to-slate-50/80 px-2.5 py-1">
              <span className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                Benefits
              </span>
            </div>
            <div className="bg-white px-2.5 pb-0.5 pt-1">
              <ul className="relative z-[1] flex max-h-[96px] min-h-0 flex-col gap-1 overflow-y-auto pr-0.5">
                {hasBenefits ? (
                  benefitRows.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[14px] font-medium leading-[1.58] text-gray-800"
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mt-[4px] flex-shrink-0 text-emerald-500"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="line-clamp-2">{b}</span>
                    </li>
                  ))
                ) : (
                  <li className="text-[14px] font-medium italic leading-snug text-slate-500">
                    No benefits listed
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="mt-auto flex min-h-0 flex-col pt-5">
            <div
              className={`mb-1 flex min-w-0 items-start gap-x-3 text-[13px] text-gray-600 ${LANG_BLOCK_MIN}`}
            >
              {languageLabels.length > 0 ? (
                <span className="inline-flex min-w-0 items-start gap-2">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0 text-gray-500"
                  >
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                  <span className="break-words font-semibold leading-snug text-gray-800 line-clamp-2">
                    {languageLabels.join(", ")}
                  </span>
                </span>
              ) : (
                <span className="text-[13.5px] text-slate-400">&nbsp;</span>
              )}
            </div>

            {/* Duration + level */}
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-gray-100 pt-1.5">
              {time_period ? (
                <span className="inline-flex items-center gap-2 text-[13px] text-gray-600 min-w-0">
                  <FaRegClock className="text-gray-500 flex-shrink-0" size={14} />
                  <span className="font-semibold text-gray-800">
                    {time_period} hrs
                  </span>
                </span>
              ) : (
                <span />
              )}

              {level && (
                <span
                  className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1 rounded-full flex-shrink-0 ${theme.chip}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} />
                  {level}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>

      {courseToDelete && openModal && (
        <ModalComponent
          showOverlay
          sidebarMode="delete"
          heading="Delete Course"
          subtitle="This action is permanent and cannot be undone."
          items={[
            {
              title: "Confirm deletion",
              desc: "Are you sure you want to delete this course?",
            },
            {
              title: "You are deleting",
              desc: name || "-",
            },
          ]}
          secondaryButtonText="Cancel"
          primaryButtonText="Delete"
          onSkip={() => setOpenModal(false)}
          onEnable={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default CourseCard;
